<!-- ContactForm.vue or similar -->
<template>
  <div class="container my-5">
    <h1 class="display-6 text-center">
      <i class="bi bi-envelope-fill me-2"></i>Contact Us</h1>
    <p class="lead text-center mb-5">
      Have questions or ready to get started? Reach out to us — we're here to help. Whether you're a student, parent, or educator, we're happy to discuss how we can support your goals.
    </p>

    <div class="row g-4 justify-content-center align-items-start">
      <!-- LEFT COLUMN -->
      <div class="col-lg-4">
        <div class="container py-4">
      <div class="card shadow-lg border-0 rounded-4">
        <div class="card-body text-center p-4">
          <h2 class="text-success fw-bold mb-3">Unlock Potential with our Expert Teaching</h2>
          <p class="text-muted fs-6 mb-3">
            We know you want the best for yourself — and so do we. Our experienced teachers help students boost their grades, build confidence, and discover a love for learning.
            Our friendly teachers make tough topics click—and your confidence soar. Whether you're aiming for top grades or just trying to keep up, we’ve got your back.
          </p>
        </div>
      </div>
    </div>

      </div>

      <!-- RIGHT COLUMN (FORM) -->
      <div class="col-lg-7">
        <form
          class="bg-dark-subtle bg-white p-4 border rounded shadow-sm"
          @submit.prevent="validateForm"
        >
          <!-- Level and Exam Board -->
          <div class="row mb-2">
            <div class="col-12 col-md-6 mb-2 mb-md-0">
              <select
                class="form-select"
                v-model="form.level"
                :class="{ 'is-invalid': showErrors && !form.level }"
              >
                <option value="" disabled selected>Select Level</option>
                <option
                  v-for="level in options.levels"
                  :key="level._id"
                  :value="level._id"
                >
                  {{ level.name }}
                </option>
              </select>
              <div class="invalid-feedback" v-if="showErrors && !form.level">
                Please select a level.
              </div>
            </div>

            <div class="col-12 col-md-6 mb-2 mb-md-0">
              <select
                class="form-select"
                v-model="form.examboard"
                :class="{ 'is-invalid': showErrors && !form.examboard }"
              >
                <option value="" disabled selected>Select Exam Board</option>
                <option
                  v-for="board in options.examboards"
                  :key="board._id"
                  :value="board._id"
                >
                  {{ board.name }}
                </option>
              </select>
              <div
                class="invalid-feedback"
                v-if="showErrors && !form.examboard"
              >
                Please select an exam board.
              </div>
            </div>
          </div>

          <!-- Subject -->
          <div class="mb-3">
            <select
              class="form-select"
              v-model="form.subject"
              :class="{ 'is-invalid': showErrors && !form.subject }"
            >
              <option value="" disabled selected>Select Subject</option>
              <option
                v-for="subject in options.subjects"
                :key="subject._id"
                :value="subject._id"
              >
                {{ subject.name }}
              </option>
            </select>
            <div class="invalid-feedback" v-if="showErrors && !form.subject">
              Please select a subject.
            </div>
          </div>

          <!-- Learning Need -->
          <div class="mb-3">
            <select
              class="form-select"
              v-model="form.learningNeed"
              :class="{ 'is-invalid': showErrors && !form.learningNeed }"
            >
              <option value="" disabled selected>Select Learning Need</option>
              <option
                v-for="need in options.learningNeeds"
                :key="need._id"
                :value="need._id"
              >
                {{ need.learningDescription }}
              </option>
            </select>
            <div
              class="invalid-feedback"
              v-if="showErrors && !form.learningNeed"
            >
              Please select a learning need.
            </div>
          </div>

          <!-- Message -->
          <div class="mb-3">
            <small class="form-text text-muted fst-italic mb-2">
              e.g. topic name, chapter, paper 1 or 2, OOP, mock exam, test questions...
            </small>
            <textarea
              class="form-control"
              placeholder="Your message here..."
              rows="4"
              v-model="form.message"
              :class="{ 'is-invalid': showErrors && !form.message }"
            ></textarea>
            <div class="invalid-feedback" v-if="showErrors && !form.message">
              Message is required.
            </div>
          </div>

          <!-- Names -->
          <div class="row mb-3">
            <div class="col-12 col-md-6 mb-2 mb-md-0">
              <input
                type="text"
                class="form-control"
                placeholder="First name"
                v-model="form.firstName"
                :class="{ 'is-invalid': showErrors && !form.firstName }"
              />
              <div
                class="invalid-feedback"
                v-if="showErrors && !form.firstName"
              >
                First name is required.
              </div>
            </div>
            <div class="col">
              <input
                type="text"
                class="form-control"
                placeholder="Last name"
                v-model="form.lastName"
                :class="{ 'is-invalid': showErrors && !form.lastName }"
              />
              <div
                class="invalid-feedback"
                v-if="showErrors && !form.lastName"
              >
                Last name is required.
              </div>
            </div>
          </div>

          <!-- Email Fields -->
          <div class="mb-3">
            <input
              type="email"
              class="form-control"
              placeholder="Your email"
              v-model="form.email1"
              :class="{ 'is-invalid': showErrors && !isValidEmail(form.email1) }"
            />
            <div
              class="invalid-feedback"
              v-if="showErrors && !isValidEmail(form.email1)"
            >
              Valid email is required.
            </div>
          </div>
          <div class="mb-3">
            <input
              type="email"
              class="form-control"
              placeholder="Confirm your email"
              v-model="form.email2"
              :class="{ 'is-invalid': showErrors && form.email1 !== form.email2 }"
            />
            <div class="invalid-feedback" v-if="showErrors && form.email1 !== form.email2">
              Emails do not match.
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="submitSuccess" class="alert alert-success mt-3">
            Your query is submitted successfully. You will be contacted by one of our staff members through your provided email address. Thank you!
          </div>

          <div v-if="submitError" class="alert alert-danger mt-3">
            {{ submitError }}
          </div>
        
          <!-- Submit Button -->
          <button
            class="btn btn-success"
            :disabled="submitting"
            @click.prevent="submitForm"
          >
            {{ submitting ? 'Submitting...' : 'Submit' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>




<script>
import axios from 'axios';
export default {
  data() {
    return {
      form: {
        level: '',
        subject: '',
        examboard: '',
        learningNeed: '',
        firstName: '',
        lastName: '',
        email1: '',
        email2: '',
        message: ''
      },
      options: {
        levels: [],
        subjects: [],
        examboards: [],
        learningNeeds: []
      },
      showErrors: false,
      isSubmitting: false,
      submitting:false,
    };
  },
  methods: {
    isValidEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    },
  async submitForm() {
      this.showErrors = true;
      this.submitError = '';
      this.submitSuccess = false;
      this.submitting = true;

      // ✅ Perform validation
      const allValid =
        this.form.level &&
        this.form.subject &&
        this.form.examboard &&
        this.form.learningNeed &&
        this.form.firstName &&
        this.form.lastName &&
        this.isValidEmail(this.form.email1) &&
        this.form.email1 === this.form.email2 &&
        this.form.message;

      if (!allValid) {
        this.submitting = false;
        return;
      }

      try {
        const response = await axios.post('https://stepedgeacademy.co.uk/api/addContactQuery', {
          firstName: this.form.firstName.trim(),
          lastName: this.form.lastName.trim(),
          email1: this.form.email1.trim(),
          level: this.form.level,
          subject: this.form.subject,
          examboard: this.form.examboard,
          learningNeed: this.form.learningNeed,
          queryDescription: this.form.message.trim()
        });

        if (response.status === 201) {
          this.submitSuccess = true;
          this.resetForm();
          this.showErrors = false;
        }
      } catch (error) {
        console.error('Submission error:', error);
        this.submitError = 'There was an error submitting your query. Please try again.';
      } finally {
        this.submitting = false;
      }
}
,
    resetForm() {
      this.form = {
        level: '',
        subject: '',
        examboard: '',
        learningNeed: '',
        name: '',
        email1: '',
        email2: '',
        message: ''
      };
      this.showErrors = false;
    },
    async fetchFormOptions() {
      try {
        const response = await  fetch('https://stepedgeacademy.co.uk/api/form-data')
        const data = await response.json();
        this.options = data;
      } catch (error) {
        console.error("Error fetching form options:", error);
        alert("Unable to load form options.");
      }
    }
  },
  mounted() {
    this.fetchFormOptions();
  }
};
</script>






<style scoped>
.contact-form {
  background-color: #ffffff;
  border: 20px solid #28a745; /* Green border */
}

.custom-input {
  border: 1.5px solid #155724 !important; /* Dark green border */
  background-color: #ffffff;
  color: #212529;
  border-radius: 4px;
  padding: 0.5rem;
}

.custom-input:focus {
  border-color: #28a745;
  box-shadow: 0 0 0 0.15rem rgba(40, 167, 69, 0.25);
}

label,
option,
select,
textarea,
input {
  color: #155724; /* dark green */
}

h4 {
  color: #1b5e20; /* deeper green heading */
}

body {
  background-color: #e8f5e9; /* light green background */
}

form select,
form textarea,
form input {
  background-color: white;
  color: #1b5e20; /* dark green text */
}

form select::placeholder,
form input::placeholder,
form textarea::placeholder {
  color: #6c757d;
}

.is-invalid {
  border-color: #dc3545 !important;
}
.invalid-feedback {
  display: block;
}
</style>
